import { DeleteOutlined } from '@ant-design/icons';
import { Button, Input } from 'antd';
import { useId } from 'react';

import type { LikertMatrixProps, ScreeningAnswerValue } from '@/interface';

const getSelectedMap = (value: ScreeningAnswerValue | undefined): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value;
};

const LikertMatrix = ({
  mode,
  options = [],
  statements = [],
  value,
  fieldIdPrefix,
  disabled = false,
  canAdd = false,
  canDelete = false,
  canUpdate = true,
  onAnswerChange,
  onOptionChange,
  onStatementChange,
  onAddOption,
  onAddStatement,
  onRemoveOption,
  onRemoveStatement,
}: LikertMatrixProps) => {
  const matrixId = useId();
  const selected = getSelectedMap(value);
  const isEditable = mode === 'editable';
  const minWidth = mode === 'editable' ? 240 + Math.max(options.length, 1) * 128 : 180 + Math.max(options.length, 1) * 64;
  const columns =
    mode === 'editable'
      ? `minmax(240px, 1.5fr) repeat(${Math.max(options.length, 1)}, minmax(128px, 1fr))`
      : `minmax(140px, 1.2fr) repeat(${Math.max(options.length, 1)}, minmax(56px, 1fr))`;

  const chooseAnswer = (statement: string, option: string) => {
    if (disabled || mode !== 'answer' || !onAnswerChange) return;
    onAnswerChange({ ...selected, [statement]: option });
  };

  return (
    <div className="space-y-3 overflow-x-auto">
      {options.length > 0 && statements.length > 0 && (
        <div className="rounded-md border border-gray-200 bg-white" style={{ minWidth }}>
          <div className="grid bg-gray-50 text-xs font-medium text-gray-600" style={{ gridTemplateColumns: columns }}>
            <div className="p-2">Statements</div>
            {options.map((option, optionIndex) => (
              <div
                key={`likert-option-${optionIndex}`}
                className={`border-l border-gray-100 ${isEditable ? 'group relative min-w-0 p-1 pr-8' : 'p-2 text-center'}`}
              >
                {isEditable ? (
                  <>
                    <Input.TextArea
                      autoSize={{ minRows: 1, maxRows: 4 }}
                      placeholder={`${optionIndex + 1}`}
                      value={option}
                      disabled={!canUpdate}
                      bordered={false}
                      className="text-center font-medium"
                      data-screening-field={fieldIdPrefix ? `${fieldIdPrefix}-option-${optionIndex}` : undefined}
                      onChange={(event) => onOptionChange?.(optionIndex, event.target.value)}
                    />
                    {canDelete && (
                      <Button
                        type="link"
                        danger
                        size="small"
                        aria-label={`Remove option ${optionIndex + 1}`}
                        icon={<DeleteOutlined />}
                        className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
                        onClick={() => onRemoveOption?.(optionIndex)}
                      />
                    )}
                  </>
                ) : (
                  option || optionIndex + 1
                )}
              </div>
            ))}
          </div>

          {statements.map((statement, statementIndex) => (
            <div
              key={`likert-statement-${statementIndex}`}
              className="grid border-t border-gray-100 text-sm"
              style={{ gridTemplateColumns: columns }}
            >
              <div className={isEditable ? 'group relative min-w-0 p-1 pr-8' : 'break-words p-2 text-gray-700'}>
                {isEditable ? (
                  <>
                    <Input.TextArea
                      autoSize={{ minRows: 1, maxRows: 5 }}
                      placeholder={`Enter Statement`}
                      value={statement}
                      disabled={!canUpdate}
                      bordered={false}
                      data-screening-field={fieldIdPrefix ? `${fieldIdPrefix}-statement-${statementIndex}` : undefined}
                      onChange={(event) => onStatementChange?.(statementIndex, event.target.value)}
                    />
                    {canDelete && (
                      <Button
                        type="link"
                        danger
                        size="small"
                        aria-label={`Remove statement ${statementIndex + 1}`}
                        icon={<DeleteOutlined />}
                        className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
                        onClick={() => onRemoveStatement?.(statementIndex)}
                      />
                    )}
                  </>
                ) : (
                  statement
                )}
              </div>

              {options.map((option, optionIndex) => {
                const checked = selected[statement] === option;
                return (
                  <label
                    key={`likert-cell-${statementIndex}-${optionIndex}`}
                    className="flex min-h-[44px] items-center justify-center border-l border-gray-100"
                  >
                    {mode === 'readonly' ? (
                      <span
                        className={`h-4 w-4 rounded-full border ${
                          checked ? 'border-primary bg-primary' : 'border-gray-300 bg-white'
                        }`}
                      />
                    ) : (
                      <input
                        type="radio"
                        name={`likert-${matrixId}-${statementIndex}`}
                        checked={mode === 'answer' ? checked : false}
                        disabled={disabled || mode !== 'answer'}
                        onChange={() => chooseAnswer(statement, option)}
                      />
                    )}
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {isEditable && (
        <div className="flex flex-wrap gap-2">
          {canAdd && (
            <Button type="dashed" onClick={onAddStatement}>
              Add statement
            </Button>
          )}
          {canAdd && (
            <Button type="dashed" onClick={onAddOption}>
              Add option
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default LikertMatrix;
